//Imports
import React, { useEffect, useMemo, useState } from "react";

//Hooks
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

//Controls
import {
    Button,
    Form,
    Input,
    Modal,
    Switch,
    Select
} from "@gpstrackit/gtc-react-controls";
import { Grid } from "@mui/material";

import { NotificationContainer, NotificationManager } from "Components/Notifications";

//Helpers
import { getCountryCodes } from "Core/data/Helpers";

//Slice
import { setFieldIdCustomerSelector } from 'Redux/actions'
import {
    workOrderCreateContact,
    workOrderEditContact,
    workOrderGetContact,
    reset,
} from "./redux/slice";

//Data
const INIT_DATA = {
    name: null,
    phone: null,
    notifyByEmail: false,
    notifyByPhone: false,
    contact: null,
};

//Styles
import './index.scss'

const CreateEditContact = ({ refresh }) => {

    //States
    const [formData, setFormData] = useState(INIT_DATA);
    const [changes, setChanges] = useState(INIT_DATA);

    const { messages } = useIntl();

    //Form refs
    const createEditContactFormRef = Form.useForm();

    //Redux
    const {
        loading,
        open,
        create,
        update,
        contactId,
        customerId,
        contact,
        error
    } = useSelector((state) => state.workOrdersCreateEditContactRedux);

    const dispatch = useDispatch();

    //Other hooks
    const title = useMemo(() => {
        return contact || contactId ? `${messages['workOrder_contact_edit_title']} ${contact ? `"${contact?.name}"` : ''}` : messages['workOrder_contact_create_title']
    }, [contact, contactId]);

    //Handlers and utils
    const handleClose = () => {
        setFormData(INIT_DATA);
        dispatch(reset());
    };

    const handleSuccess = () => {
        NotificationManager.success(
            messages['workOrder_contact_contact_saved'],
            messages["success"],
            3000,
            null,
            null,
            "success"
        );
        refresh && refresh()
        handleClose();
    };

    const handleCreateEditContact = () => {

        const isEditing = !!contactId;

        let payload = {};

        if (isEditing) {
            if (changes.name || changes.email || changes.notifyByEmail || changes.notifyByPhone || changes.codeCountry || changes.phone) {
                payload = {
                    id: contactId,
                    entityName: "CompanyCustomer",
                    ...(changes.name && { name: formData?.name }),
                    ...((changes.codeCountry || changes.phone) && { phone: formData?.phone, countryCode: formData?.codeCountry?.code }),
                    ...(changes.email && { email: formData?.email }),
                    ...(changes.notifyByPhone && { notifyByPhone: formData?.notifyByPhone, phone: formData?.phone, countryCode: formData?.codeCountry?.code }),
                    ...(changes.notifyByEmail && { notifyByEmail: formData?.notifyByEmail, email: formData?.email }),
                };
            } else {
                NotificationManager.info(
                    messages['workOrder_contact_no_changes_to_save'],
                    messages["info"],
                    3000,
                    null,
                    null,
                    "success"
                );
                return
            }
        } else {
            payload = {
                name: formData?.name,
                entityId: customerId,
                entityName: "CompanyCustomer",
                phone: formData?.phone,
                email: formData?.email,
                countryCode: formData?.countryCode?.code,
                notifyByPhone: formData?.notifyByPhone,
                notifyByEmail: formData?.notifyByEmail
            };
        }
        if (!isEditing) {
            dispatch(workOrderCreateContact(payload));
        } else {
            dispatch(workOrderEditContact(payload));
        }
    }

    useEffect(() => {
        if (open && contactId) {
            dispatch(workOrderGetContact({ id: contactId }))
        }
    }, [open])

    useEffect(() => {
        if (create || update) {
            if (create?.id) {
                dispatch(setFieldIdCustomerSelector({ value: create?.id, field: 'contactId' }))
            }
            handleSuccess()
        }
    }, [create, update])

    useEffect(() => {
        if (contact) {
            const countryCode = getCountryCodes()
            let currentCountryCode = countryCode.find((item) => item?.code === contact?.countryCode)

            let currentData = {
                name: contact?.name,
                email: contact?.email,
                notifyByEmail: contact?.notifyByEmail,
                notifyByPhone: contact?.notifyByPhone,
                countryCode: { ...currentCountryCode, label: `${currentCountryCode.code} (+${currentCountryCode.id})` },
                phone: contact?.phone
            }
            setFormData({ ...formData, ...currentData })
            createEditContactFormRef.setValues({ ...currentData })
        }
    }, [contact])

    useEffect(() => {
        if (error) {
            let errorMessage = "";
            const errorLower = error?.error?.toLowerCase();

            const errorMessages = {
                "duplicated name": messages['workOrder_contact_contactNameIsAlreadyInUse'],
                "duplicated phone": messages['workOrder_ccontact_contactPhoneIsAlreadyInUse'],
                "duplicated email": messages['workOrder_contact_contactEmailIsAlreadyInUse']
            };

            for (const key in errorMessages) {
                if (errorLower?.includes(key)) {
                    errorMessage = errorMessages[key];
                    break;
                }
            }

            NotificationManager.error(
                errorMessage || messages['errorMessage'],
                messages["error"],
                3000,
            );

            dispatch(reset({ error: null }));
        }
    }, [error])

    return (
        <Modal
            title={title && title.length > 48 ? title.substring(0, 45) + '..."' : title}
            className="modal-create-entity-contact"
            open={open}
            handleClose={handleClose}
            position="right"
            style={{ zIndex: "auto" }}
            size="small"
            loading={loading}
            footer={
                <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                    <Button
                        isDisabled={loading}
                        style={{ marginRight: 8 }}
                        onClick={handleClose}
                        variant="secondary"
                    >
                        {messages["cancel"]}
                    </Button>
                    <Button
                        isDisabled={loading}
                        onClick={() => {
                            createEditContactFormRef?.submit()
                        }}
                        variant="primary"
                    >
                        {messages["save"]}
                    </Button>
                </div>
            }
        >
            <div style={{ padding: 24 }}>
                <Form
                    ref={createEditContactFormRef.ref}
                    loading={loading}
                    onValidSubmit={() => handleCreateEditContact()}
                    onChangeValues={() => { }}
                >
                    <Form.Item
                        name="name"
                        label={messages['workOrder_contact_name']}
                        rules={[{ required: true, message: 'required_field' }]}
                    >
                        <Input
                            id="name"
                            type="text"
                            placeholder={messages['workOrder_contact_name']}
                            onChange={(value) => {
                                setFormData({ ...formData, name: value })
                                setChanges({ ...changes, name: true })
                            }}
                            maxLength={50}
                        />
                    </Form.Item>
                    <Grid container spacing={1}>
                        <Grid item xs={5}>
                            <Form.Item
                                name="countryCode"
                                label={messages['workOrder_contact_codeCountry']}
                                rules={[{ required: true, message: 'required_field' }]}
                            >
                                <Select
                                    id="countryCode"
                                    options={getCountryCodes().map((item) => ({ ...item, label: `${item.code} (+${item.id})` })) || []}
                                    placeholder={messages['workOrder_contact_codeCountry']}
                                    valueField="id"
                                    textField="label"
                                    style={{ width: "100%", fontFamily: '"Outfit", sans-serif' }}
                                    isSearchable
                                    onChange={(value) => {
                                        setFormData({ ...formData, countryCode: value })
                                        setChanges({ ...changes, countryCode: true })
                                    }}
                                    className="customer-container-code-country-selector"
                                />
                            </Form.Item>
                        </Grid>
                        <Grid item xs={7}>
                            <Form.Item
                                name="phone"
                                label={messages['workOrder_createEditCustomer_phone']}
                                rules={[
                                    { required: true, message: 'required_field' },
                                    { phone: true }
                                ]}
                            >
                                <Input
                                    id="phone"
                                    type="phone"
                                    maxLength={10}
                                    minLength={10}
                                    placeholder={messages['workOrder_createEditCustomer_phone']}
                                    onChange={(value) => {
                                        setFormData({ ...formData, phone: value })
                                        setChanges({ ...changes, phone: true })
                                    }}
                                />
                            </Form.Item>
                        </Grid>
                    </Grid>
                    <Form.Item
                        name="email"
                        label={messages['workOrder_contact_email']}
                        rules={[
                            { required: true, message: 'required_field' },
                            { email: true }
                        ]}
                    >
                        <Input
                            id="email"
                            type="email"
                            placeholder={messages['workOrder_contact_email']}
                            onChange={(value) => {
                                setFormData({ ...formData, email: value })
                                setChanges({ ...changes, email: true })
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="notifyByPhone"
                        label={messages['workOrder_contact_notifyByPhone']}
                    >
                        <Switch
                            id="notifyByPhone"
                            optionLabels={["", ""]}
                            size={"small"}
                            onChange={(value) => {
                                setFormData({ ...formData, notifyByPhone: value })
                                setChanges({ ...changes, notifyByPhone: true })
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="notifyByEmail"
                        label={messages['workOrder_contact_notifyByEmail']}
                    >
                        <Switch
                            id="notifyByEmail"
                            optionLabels={["", ""]}
                            size={"small"}
                            onChange={(value) => {
                                setFormData({ ...formData, notifyByEmail: value })
                                setChanges({ ...changes, notifyByEmail: true })
                            }}
                        />
                    </Form.Item>
                </Form>
            </div>
            <NotificationContainer />
        </Modal >
    );
};

export default CreateEditContact;